import { LookupStorage, system } from "portalframework";
import * as ko from "knockout";
import * as $ from "jquery";
import * as entities from "entities";
import * as _ from "lodash";
import * as entitiesE from "entitiesE";
import * as toastr from "toastr";
import {
  attachment_wrapper,
  searchResult,
  taskHistory,
  TaskManagementSearchCriteria,
  taskStep,
  taskTab,
  TaskTrackingUrl,
  task_management,
  task_management_extended,
  car_dto,
  CarStockSearchCriteria,
  PartUsage,
} from "organized";
import labels from "app/labels";
import { AttachmentResultDTO, downloadAttachmentFromBase64, dynamicFieldsService } from "services";
import { portalHttpService } from "./_common/httpService";
import { TASK_PAGE } from "components/task_management/TaskPage";
import { BaseService } from "./_common/_baseService";

class TaskManagementService extends BaseService {
  constructor() {
    super("Task_Managment");
  }

  public getTaskScreenData = async (requiredLookups: string[], task_type: string = null): Promise<void> => {
    let lookupsToFetch = _.difference(requiredLookups, LookupStorage.lookupStorage.lookupNames());
    let params = ko.toJSON({ requiredLookups: lookupsToFetch });
    const data = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/GetTaskScreenData`,
      params,
    );

    this.setLookupData(lookupsToFetch, data.results);

    const result: any = await this.getPortalTaskStatus();
    let list = result.task_status;
    if (list) {
      LookupStorage.lookupStorage.add(new LookupStorage.Lookup("TASK_STATUS", list));
    }
  };

  public getTaskById = async (
    task_id: string,
    task_join_type: boolean = false,
    is_my_task: boolean = true,
  ): Promise<task_management_extended> => {
    let params = ko.toJSON({ task_id: task_id, task_join_type: task_join_type, is_my_task });
    let url = `${system.apiURL()}api/${this.controllerName}/GetTaskById`;
    const taskResult = await portalHttpService.post<any>(`${url}`, params);

    let task: task_management_extended = null;
    if (taskResult) {
      task = new task_management_extended(taskResult);
    } else {
      throw new Error(labels.descr("cc_P_TASK_NOT_FOUND"));
    }
    return task;
  };

  public searchTasks = async (
    criteria: TaskManagementSearchCriteria,
  ): Promise<searchResult<task_management_extended[]>> => {
    const data = await portalHttpService.post<any>(`${this.baseApiUrl()}/GetTasksList`, ko.toJSON(criteria));

    const result = data.result.map((task) => new task_management_extended(task));
    return new searchResult<task_management_extended[]>({
      count: data.count,
      result,
    });
  };

  public getHistoryTasksByTaskId = async (task_id: string): Promise<taskHistory[]> => {
    const tasks: any[] = await portalHttpService.get<any>(
      system.apiURL() + `api/${this.controllerName}/GetHistoryTasksByTaskId`,
      {
        task_id: task_id,
      },
    );
    return tasks.map((entity) => new taskHistory(entity));
  };

  public getHistoryTasksByProductId = async (product_id: string): Promise<taskHistory[]> => {
    const tasks: any[] = await portalHttpService.get<any>(
      `${system.apiURL()}api/${this.controllerName}/GetHistoryTasksByProductId`,
      {
        product_id,
      },
    );
    return tasks.map((entity) => new taskHistory(entity));
  };

  getHistoryTasksByRequestId = async (requestId: string): Promise<taskHistory[]> => {
    const tasks: any[] = await portalHttpService.get<any>(
      `${system.apiURL()}api/${this.controllerName}/GetHistoryTasksByRequestId`,
      {
        requestId,
      },
    );
    return tasks.map((t) => new taskHistory(t));
  };

  public exportFile = async (
    criteria: TaskManagementSearchCriteria,
    headers: any[],
    offset: any,
    pageType: TASK_PAGE,
  ): Promise<void> => {
    // get headers not empty
    let headersLabels = headers
      .map((h) => h.label)
      .filter((h) => h)
      .join(";");

    let param = {
      criteria: ko.toJS(criteria),
      headersLabels,
      timeZone: offset,
      pageType: pageType,
    };

    const myUrlWithParams = new URL(`${this.baseApiUrl()}/ExportFile`);
    myUrlWithParams.searchParams.append("searchParam", ko.toJSON(param));

    const fileResult: AttachmentResultDTO = await portalHttpService.get<any>(myUrlWithParams.href);
    downloadAttachmentFromBase64(fileResult);
  };

  public saveAll = async (data: any): Promise<void> => {
    await portalHttpService.post<any>(system.apiURL() + `api/${this.controllerName}/SaveTaskData`, data);
  };

  protected setLookupData = (lookupsToFetch: string[], results: any) => {
    _.each(lookupsToFetch, (lookupName) => {
      let list = results[lookupName];
      if (list) {
        LookupStorage.lookupStorage.add(new LookupStorage.Lookup(lookupName, list));
      }
    });
  };

  public addNewPartUsage = async (partUsage: PartUsage): Promise<PartUsage> => {
    let url = `${system.apiURL()}api/${this.controllerName}/addNewPartUsage`;
    const data = await portalHttpService.post<any>(`${url}`, ko.toJSON(partUsage));
    let request: PartUsage = null;
    if (data.C_TASK_MEMBER_VIEW && data.C_TASK_MEMBER_VIEW.c_task_member_view) {
      request = new PartUsage(data.C_TASK_MEMBER_VIEW.c_task_member_view);
    }

    return request;
  };

  getServiceOrdersCount = async (task_id: string): Promise<number> => {
    return await portalHttpService.get<number>(`${this.baseApiUrl()}/GetServiceOrdersCount/${task_id}`);
  };

  public getAllPartUsage = async (task_id: string): Promise<PartUsage[]> => {
    const data = await portalHttpService.post<any>(
      `${this.baseApiUrl()}/GetAllPartUsage`,
      ko.toJSON({ task_id: task_id }),
    );

    let map: PartUsage[];
    if (data.PART_USAGE && data.PART_USAGE.part_usage instanceof Array) {
      map = _.map(data.PART_USAGE.part_usage, (entity) => new PartUsage(entity));
    } else {
      if (data.PART_USAGE.part_usage) map = new Array(new PartUsage(data.PART_USAGE.part_usage));
    }

    return map;
  };

  public getAllReadings = async (task_id: string): Promise<PartUsage[]> => {
    var requiredLookups = ["PART_LINE_CODE"];
    let lookupsToFetch = _.difference(requiredLookups, LookupStorage.lookupStorage.lookupNames());

    const result = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/GetReadings`,
      ko.toJSON({
        task_id: task_id,
        requiredLookups: requiredLookups,
      }),
    );
    let map;
    if (result.task.task && result.task.task.meter_readings instanceof Array) {
      map = _.map(result.task.task.meter_readings, (entity) => new entities.Readings(entity));
    } else {
      if (result.task.task && result.task.task.meter_readings)
        map = new Array(new entities.Readings(result.task.task.meter_readings));
    }
    this.setLookupData(lookupsToFetch, result.results);

    return map;
  };

  public getAllTechReadings = async (task_id: string): Promise<PartUsage[]> => {
    const data = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/GetTechReadings`,
      ko.toJSON({
        task_id: task_id,
      }),
    );

    let map = new Array();
    if (data.c_tech_meter_readings && data.c_tech_meter_readings.c_tech_meter_readings instanceof Array) {
      map = _.map(data.c_tech_meter_readings.c_tech_meter_readings, (entity) => new entities.TechReadings(entity));
    } else {
      if (data.c_tech_meter_readings && data.c_tech_meter_readings.c_tech_meter_readings)
        map = new Array(new entities.TechReadings(data.c_tech_meter_readings.c_tech_meter_readings));
    }
    if (data.meter_readings && data.meter_readings.meter_readings instanceof Array) {
      map = map.concat(_.map(data.meter_readings.meter_readings, (entity) => new entities.Readings(entity)));
    } else {
      if (data.meter_readings && data.meter_readings.meter_readings)
        map.push(new Array(new entities.Readings(data.meter_readings.meter_readings)));
    }
    return _.orderBy(
      map,
      function (o) {
        return parseInt(o.meter_reading_id(), 10);
      },
      ["desc"],
    );
  };

  public getMonitoring = async (task_id: string) => {
    const data = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/GetMonitoring`,
      ko.toJSON({ task_id }),
    );
    return data.task;
  };

  public removeAttachment = async (task_id, attachment_id): Promise<void> => {
    await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/RemoveAttachment`,
      ko.toJSON({
        attachment_id: attachment_id,
        task_id: task_id,
      }),
    );
  };

  public creatRequestAttachment = (request_id, attachment) => {
    var request_attachment_data = {
      request_id: request_id,
      attachment: {
        file: attachment.file,
        attachment_name: attachment.name,
        attachment_type: attachment.attachment_type,
        attachment_description: attachment.description,
        file_type: attachment.contentType,
      },
    };

    return new attachment_wrapper(request_attachment_data);
  };

  dataURLtoBlob(dataurl) {
    var arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  getJournalNotes = async (request_id: string, task_id: string): Promise<entitiesE.screenNote[]> => {
    const data = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/GetJournalNotes`,
      ko.toJSON({
        request_id: request_id,
        task_id: task_id,
      }),
    );

    let notesUpdate = entities.loadRelation(data, "screenNote", "array");
    return notesUpdate();
  };

  getPortalTaskNotes = async (request_id: string, task_id: string): Promise<entitiesE.screenNote[]> => {
    const data = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/GetPortalTaskNotes`,
      ko.toJSON({
        request_id: request_id,
        task_id: task_id,
      }),
    );

    let notesUpdate = entities.loadRelation(data, "screenNote", "array");
    return notesUpdate();
  };

  GetAllMaterialCodes = (): Promise<any[]> => {
    return this.genericCall("GetAllMaterialCodes");
  };

  getPortalTaskStatus = (): Promise<any[]> => {
    return this.genericCall("getPortalTaskStatus");
  };

  getServiceModelDescription = (): Promise<any[]> => {
    return this.genericCall("ServiceModelDescription");
  };

  private genericCall = async (method: any): Promise<any[]> => {
    return await portalHttpService.post<any>(system.apiURL() + `api/${this.controllerName}/${method}`);
  };

  addNote = async (task_id: string, note: string, type: string, key: string): Promise<entitiesE.screenNote[]> => {
    var data = ko.toJSON({ task_id: task_id, note: note, type: type, key: key });

    const result = await portalHttpService.post<any>(system.apiURL() + `api/${this.controllerName}/addNote`, data);
    let notesUpdate = entities.loadRelation(result, "screenNote", "array");
    toastr.success(labels.descr("cc_Note_Added"));
    return notesUpdate();
  };

  getAvailableServiceReport = async (task_type: string) => {
    const myUrlWithParams = new URL(`${this.baseApiUrl()}/GetAvailableServiceReport`);
    myUrlWithParams.searchParams.append("task_type", task_type);
    return await portalHttpService.get<any>(myUrlWithParams.href);
  };

  printReportPDF = async (task_id: string, task_type: string): Promise<void> => {
    const myUrlWithParams = new URL(`${this.baseApiUrl()}/GetReportPDF`);
    myUrlWithParams.searchParams.append("task_type", task_type);
    myUrlWithParams.searchParams.append("task_id", task_id);

    const base64EncodedPDF = await portalHttpService.get<any>(myUrlWithParams.href);
    const win = window.open();
    win.document.write(
      '<iframe src="data:application/pdf;base64,' +
        base64EncodedPDF +
        '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>',
    );
  };

  getPossibleNextSteps = async (task_id: string): Promise<taskStep[]> => {
    const data = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/PossibleNextSteps`,
      ko.toJSON({ task_id: task_id }),
    );

    let nextSteps;
    if (Array.isArray(data.K_PORTAL_TASK_NEXTSTEPS.k_portal_task_nextsteps)) {
      nextSteps = _.map(data.K_PORTAL_TASK_NEXTSTEPS.k_portal_task_nextsteps, (entity) => new taskStep(entity));
    } else if (
      typeof data.K_PORTAL_TASK_NEXTSTEPS.k_portal_task_nextsteps !== "undefined" &&
      Object.keys(data.K_PORTAL_TASK_NEXTSTEPS.k_portal_task_nextsteps).length !== 0
    ) {
      nextSteps = [new taskStep(data.K_PORTAL_TASK_NEXTSTEPS.k_portal_task_nextsteps)];
    } else {
      nextSteps = [];
    }
    return nextSteps;
  };

  getPortalTaskTabs = async (task_type: string): Promise<taskTab[]> => {
    const tabs: taskTab[] = await portalHttpService.post(
      `${this.baseApiUrl()}/GetPortalTaskTabs`,
      ko.toJSON({ task_type: task_type }),
    );
    return tabs;
  };

  getTaskStatus = async (task_id: string): Promise<any> => {
    const data = await portalHttpService.post<any>(
      system.apiURL() + `api/${this.controllerName}/GetTaskStatus`,
      ko.toJSON({ task_id: task_id }),
    );
    return data?.task?.task;
  };

  getTaskTrackingUrl = async (task_id: string, task_type: string): Promise<TaskTrackingUrl[]> => {
    const responseUrls: any[] = await portalHttpService.post(
      `${this.baseApiUrl()}/GetTaskTrackingUrl`,
      ko.toJSON({ task_id }),
    );
    return responseUrls.map((url) => {
      url.task_type = task_type;
      return new TaskTrackingUrl(url);
    });
  };

  getTaskMetadata = async (tabName: string, params: any) => {
    return await portalHttpService.post<any>(`${system.apiURL()}api/TaskManagementMetadata/get/${tabName}`, params);
  };

  saveTaskMetadata = async (tabName: string, params: any) => {
    return await portalHttpService.post<any>(`${system.apiURL()}api/TaskManagementMetadata/${tabName}`, params);
  };

  deleteTaskMetadata = async (tabName: string, params: any) => {
    return await portalHttpService.post<any>(`${system.apiURL()}api/TaskManagementMetadata/delete/${tabName}`, params);
  };

  getCarStock = async (criteria: CarStockSearchCriteria): Promise<searchResult<car_dto[]>> => {
    const data = await portalHttpService.post<any>(`${this.baseApiUrl()}/GetCarStock`, ko.toJSON(criteria));

    const result = data?.Data?.map((car) => new car_dto(car)) || [];
    const res = new searchResult<car_dto[]>({
      count: data?.count || 0,
      result,
    });

    res["isTeamStock"] = data?.isTeamStock || false;

    return res;
  };

  public searchTaskParts = async (part_id: string, model_id: string, team_id: string): Promise<any[]> => {
    const urlResult = new URL(`${this.baseApiUrl()}/SearchTaskParts`);
    urlResult.searchParams.append("part_id", part_id);
    urlResult.searchParams.append("model_id", model_id);
    urlResult.searchParams.append("team_id", team_id);

    const results: any[] = await portalHttpService.get(urlResult.href);
    results.forEach((p) => {
      p.listDescr = `${p.part_id} - ${p.descr}`;
    });
    return results;
  };
}

export const taskManagementService = new TaskManagementService();
